import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Table from '../../../../../components/Table';
import GlobalTableEditDropdown from '../../../../../components/GlobalTableEditDropdown';
import { myIcons } from '../../../../../helper/icons';
import { Fonts, Palette } from '../../../../../components/utils';
import {
  BOOKINGS_STATUS_PILL_COLORS,
  BookingsStatusPill
} from '../../../../bookings/V2/bookingsStatusBadgeStyles';
import { IconWithBg, resolveIconSrc } from '../../../../../components/IconSelector';
import {
  formatAvailabilityDisplayLines,
  getAmenityStatusLabel,
  isAmenityCurrentlyOpen
} from './amenitiesListDisplayHelpers';
import { formatAmenityFeeDisplay } from './amenityPaymentConfigV2';

const AmenitiesListTableWrap = styled.div`
  flex-grow: 1;
  overflow: auto;
  width: 100%;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  table tbody tr,
  table tbody tr td {
    border-bottom: none !important;
  }

  thead {
    border-bottom: 0.5px solid ${Palette.BLACK};
  }

  thead tr:first-child {
    border-bottom: none;
  }

  thead tr:first-child th {
    height: 56px;
    max-height: 56px;
    padding: 10px;
    box-sizing: border-box;
    color: ${Palette.BLACK} !important;
    font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
    font-size: 12px;
    font-weight: 400 !important;
    font-style: normal;
    line-height: 24px;
    text-align: left;
    vertical-align: middle;
    border-bottom: none !important;
    border-top: none !important;
    background-color: ${Palette.WHITE};

    &:hover {
      cursor: default;
    }

    svg {
      display: none !important;
    }
  }

  thead tr:first-child th:last-child {
    width: 56px;
    text-align: center;
  }

  tbody tr {
    border-bottom: none !important;
  }

  tbody tr:hover {
    background-color: ${Palette.GHOST_WHITE};
    cursor: pointer;
  }

  tbody td {
    min-height: 40px;
    margin: 0;
    padding: 10px;
    box-sizing: border-box;
    color: ${Palette.BLACK} !important;
    font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 24px;
    text-align: left;
    vertical-align: middle;
    border-top: none !important;
    border-bottom: none !important;
  }

  tbody td.amenities-list-name-cell {
    min-width: 85px;
    vertical-align: middle;
  }

  tbody td.amenities-list-hours-cell {
    width: 160px;
    min-width: 160px;
    max-width: 160px;
    vertical-align: middle;
  }

  tbody td.amenities-list-approval-cell {
    text-align: left;
    vertical-align: middle;
  }

  tbody td.amenities-list-fee-cell {
    text-align: left;
    vertical-align: middle;
  }

  tbody td.amenities-list-options-cell {
    width: 56px;
    min-width: 56px;
    max-width: 56px;
    padding: 10px 24px;
    text-align: center;
    vertical-align: middle;
  }

  tbody td.amenities-list-options-cell .tableEditDropdown {
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  tbody td.amenities-list-options-cell .dropdown-toggle {
    padding: 0;
    margin: 0;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    line-height: 0;

    &::after {
      display: none;
    }

    &:hover,
    &:focus {
      background: transparent !important;
    }
  }

  tbody td.amenities-list-options-cell .edit-container:hover {
    background-color: inherit;
  }
`;

const AmenitiesListNameCell = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const AmenitiesListNameLabel = styled.div`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 16px;
  word-wrap: break-word;
`;

const AmenitiesListHoursStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
`;

const AmenitiesListHoursLine = styled.div`
  color: ${Palette.CONTENT_TERTIARY};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  word-wrap: break-word;
`;

const AmenityStatusPillWrap = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 0.5px solid ${({ $border }) => $border};
  background-color: ${({ $background }) => $background};
  box-sizing: border-box;
  white-space: nowrap;
`;

const AmenityStatusPillDotWrap = styled.span`
  display: flex;
  width: 12px;
  height: 12px;
  overflow: hidden;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const AmenityStatusPillDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
`;

const AmenityStatusPillLabel = styled.span`
  color: ${({ $color }) => $color};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 16px;
`;

const ACTIVE_STATUS_PILL_COLORS = {
  background: Palette.OCEANIC_100,
  color: Palette.HAZY_BLUE,
  border: Palette.BORDER_STRONG
};

const CLOSED_STATUS_PILL_COLORS = BOOKINGS_STATUS_PILL_COLORS.Cancelled;

export const AmenityStatusPill = ({ isOpen }) => {
  const { t } = useTranslation();

  if (isOpen === null) {
    return <AmenitiesListHoursLine>—</AmenitiesListHoursLine>;
  }

  const colors = isOpen ? ACTIVE_STATUS_PILL_COLORS : CLOSED_STATUS_PILL_COLORS;

  return (
    <AmenityStatusPillWrap
      $background={colors.background}
      $border={colors.border}>
      <AmenityStatusPillDotWrap>
        <AmenityStatusPillDot $color={colors.border} />
      </AmenityStatusPillDotWrap>
      <AmenityStatusPillLabel $color={colors.color}>
        {getAmenityStatusLabel(isOpen, t)}
      </AmenityStatusPillLabel>
    </AmenityStatusPillWrap>
  );
};

const AmenityBookingTypePill = ({ allowBooking }) => {
  const { t } = useTranslation();
  const colors = allowBooking
    ? ACTIVE_STATUS_PILL_COLORS
    : BOOKINGS_STATUS_PILL_COLORS.Reserved;
  const label = allowBooking ? t('Reserve') : t('Drop in');

  return (
    <BookingsStatusPill
      $background={colors.background}
      $color={colors.color}
      $border={colors.border}>
      {label}
    </BookingsStatusPill>
  );
};

const AmenityManagerApprovalPill = ({ requireManagerApproval }) => {
  const { t } = useTranslation();

  if (requireManagerApproval) {
    const colors = ACTIVE_STATUS_PILL_COLORS;
    return (
      <AmenityStatusPillWrap
        $background={colors.background}
        $border={colors.border}>
        <AmenityStatusPillDotWrap>
          <AmenityStatusPillDot $color={colors.border} />
        </AmenityStatusPillDotWrap>
        <AmenityStatusPillLabel $color={colors.color}>
          {t('Yes')}
        </AmenityStatusPillLabel>
      </AmenityStatusPillWrap>
    );
  }

  return <AmenitiesListHoursLine>{t('No')}</AmenitiesListHoursLine>;
};

const AmenitiesListView = ({
  items,
  loading,
  statusTimezone,
  onRowClick,
  onEdit,
  onDetail,
  onBooking,
  setShowModal
}) => {
  const { t } = useTranslation();

  const fields = useMemo(
    () => [
      { key: 'amenities', label: t('Amenities'), sorter: false, filter: false },
      {
        key: 'hours',
        label: t('Hours'),
        sorter: false,
        filter: false,
        _style: { width: '160px' }
      },
      { key: 'status', label: t('Status'), sorter: false, filter: false },
      { key: 'booking_type', label: t('Booking Type'), sorter: false, filter: false },
      {
        key: 'manager_approval',
        label: t('Manager Approval'),
        sorter: false,
        filter: false
      },
      {
        key: 'fee',
        label: t('Fee'),
        sorter: false,
        filter: false
      },
      {
        key: 'options',
        label: '',
        sorter: false,
        filter: false,
        _style: { width: '56px' }
      }
    ],
    [t]
  );

  const handleSpecificColClick = (item, index, col) => {
    if (col !== 'options') {
      onRowClick(item);
    }
  };

  const stopRowClick = (e) => {
    e.stopPropagation();
  };

  const renderAmenityIcon = (item) => {
    const iconSrc = resolveIconSrc(item.icon);
    const typeId = item.type_id != null ? parseInt(item.type_id, 10) : NaN;
    const fallbackIcon = !Number.isNaN(typeId) ? myIcons[typeId > 11 ? 1 : typeId] : null;

    return <IconWithBg iconSrc={iconSrc} fallbackIcon={fallbackIcon} />;
  };

  return (
    <AmenitiesListTableWrap>
      <Table
        items={items.length ? items : []}
        loading={loading}
        fields={fields}
        onRowClick={handleSpecificColClick}
        clickableRows
        sorter={false}
        scopedSlots={{
          amenities: (item) => (
            <td className="amenities-list-name-cell">
              <AmenitiesListNameCell>
                {renderAmenityIcon(item)}
                <AmenitiesListNameLabel>{item.description || '—'}</AmenitiesListNameLabel>
              </AmenitiesListNameCell>
            </td>
          ),

          hours: (item) => {
            const lines = formatAvailabilityDisplayLines(item.availability_display);
            return (
              <td className="amenities-list-hours-cell">
                {lines ? (
                  <AmenitiesListHoursStack>
                    {lines.map((line, idx) => (
                      <AmenitiesListHoursLine key={`${idx}-${line}`}>{line}</AmenitiesListHoursLine>
                    ))}
                  </AmenitiesListHoursStack>
                ) : (
                  <AmenitiesListHoursLine>—</AmenitiesListHoursLine>
                )}
              </td>
            );
          },

          status: (item) => (
            <td>
              <AmenityStatusPill
                isOpen={isAmenityCurrentlyOpen(item.amenities_schedules, statusTimezone)}
              />
            </td>
          ),

          booking_type: (item) => (
            <td>
              <AmenityBookingTypePill allowBooking={item.allow_booking} />
            </td>
          ),

          manager_approval: (item) => (
            <td className="amenities-list-approval-cell">
              <AmenityManagerApprovalPill requireManagerApproval={item.require_manager_approval} />
            </td>
          ),

          fee: (item) => (
            <td className="amenities-list-fee-cell">
              <AmenitiesListHoursLine>{formatAmenityFeeDisplay(item, t)}</AmenitiesListHoursLine>
            </td>
          ),

          options: (item) => (
            <td
              className="amenities-list-options-cell edit-container"
              onClick={stopRowClick}>
              <GlobalTableEditDropdown
                tableName="amenities"
                item={item}
                handleEdit={onEdit}
                handleDetails={onDetail}
                handleBooking={onBooking}
                setShowModal={setShowModal}
              />
            </td>
          )
        }}
      />
    </AmenitiesListTableWrap>
  );
};

export default AmenitiesListView;
